/** @module components/cloze-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import {View} from '@lernetz/ts-lib/src/core/view/View';

import {CanShowCorrectness} from '../quiz';


/**
 * Specifies the initial state of a [[SelectSpanChoice]].
 */
export interface SelectSpanChoiceInitialState {

    /**
     * Sets whether this is the correct choice.
     */
    correct: boolean;

    /**
     * Sets whether this should should be initially selected. Defaults to `false`.
     */
    selected?: boolean;

    /**
     * Sets whether the correctness of this choice should be initially displayed.
     * Defaults to `false`.
     */
    showCorrectness?: boolean;

    /**
     * Sets the text that should be displayed for this choice.
     */
    text: string;
}


/**
 * A choice for a cloze-dropdown
 */
export class SelectSpanChoice extends Model implements CanShowCorrectness {

   /**
     * Initializes a new instance of the [[ClozeChoice]] class.
     *
     * @param initialState Specifies the initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: SelectSpanChoiceInitialState, data: object = {}) {
        super({
            showCorrectness: false,
            correct: false,
            selected: false,
            text: '',
            ...initialState,
            ...data
        });
    }

    /**
     * Gets whether this is the correct choice.
     */
    public readonly correct: boolean;

    /**
     * Gets or sets whether this choice is currently selected.
     */
    public selected: boolean;

    /**
     * Gets or sets whether the correctness of this choice should be shown.
     */
    public showCorrectness: boolean;

    /**
     * Gets the text that is displayed for this choice.
     */
    public readonly text: string;
}


/**
 * View class accompanying [[SelectSpanChoice]] that makes sure the initial [[selected]] state
 * is correctly transferred into the DOM.
 */
export class SelectSpanChoiceView extends View<SelectSpanChoice> {

    constructor() {
        super({});
    }

    init() {
        this.node.innerHTML = this.data.text;

        // The `selected` attribute only specifies whether the option is *initially* selected,
        // therefore we don't need to keep it in sync with `this.data.selected` later on:
        if (this.data.selected) {
            this.node.setAttribute('selected', '');
        }
    }
}
